import React, { useState } from 'react'
import { DiningOptionBehavior } from 'src/apollo/onlineOrdering'
import {
  useFulfillment,
  useOOLocations
} from 'public/components/online_ordering/FulfillmentContext'
import { useDelivery } from 'public/components/online_ordering/DeliveryContext'
import { formatDeliveryAddress } from 'src/public/components/online_ordering/address_configs'
import { ChevronRightIcon, LocationIcon } from '@toasttab/buffet-pui-icons'
import { t, Trans } from 'src/translations'
import { useRestaurant } from 'shared/components/common/restaurant_context/RestaurantContext'
import { formatAddress } from 'shared/components/common/form_input/LocationInput'
import ChangeLocationModal from 'public/components/default_template/online_ordering/location_selection/ChangeLocationModal'
import Skeleton from 'react-loading-skeleton'
import { useCart } from 'public/components/online_ordering/CartContext'
import { useFlags } from 'launchdarkly-react-client-sdk'

import useTracker from 'src/lib/js/hooks/useTracker'
import { TrackingEvents } from 'src/shared/components/common/tracking/TrackingEvents'

const DiningBehaviorSummary = () => {
  const { orderingDisabled } = useRestaurant()
  const { loadingFulfillment, fulfillmentData } = useFulfillment()
  const { loadingCart } = useCart()

  if (orderingDisabled) {
    return null
  }
  if (loadingFulfillment || loadingCart) {
    return (
      <Skeleton
        className='loadingSkeleton'
        containerTestId='fulfillment-time-loading-skeleton'
      />
    )
  }

  return fulfillmentData?.cartFulfillmentData?.diningOptionBehavior ===
    DiningOptionBehavior.Delivery ? (
    <DeliveryAddressSummary />
  ) : (
    <PickupSummary />
  )
}

const DeliveryAddressSummary = () => {
  const { fulfillmentData, selectedFulfillmentType } = useFulfillment()
  const { setDeliveryModalOpen } = useDelivery()
  const { ooRestaurant } = useRestaurant()
  const { track } = useTracker()

  const deliveryAddress = fulfillmentData?.cartFulfillmentData?.deliveryInfo
  const hasValidAddress = deliveryAddress?.address1
  const { intlAddressCountryLogicErrorHandling } = useFlags()

  const handleClick = () => {
    track(TrackingEvents.DINING_BEHAVIOR_SUMMARY_CLICKED, {
      diningOption: DiningOptionBehavior.Delivery,
      diningOptionStatus: hasValidAddress ? 'valid' : 'delivery-address-absent',
      fulfillmentTimeType: selectedFulfillmentType ?? undefined
    })
    setDeliveryModalOpen(true)
  }

  if (hasValidAddress) {
    // TODO: final touches - what address properties do we include here: full, zip, addr2?
    const displayAddress = formatDeliveryAddress(
      deliveryAddress,
      {
        country: ooRestaurant?.i18n.country
      },
      intlAddressCountryLogicErrorHandling
    )

    return (
      <button
        id='delivery-address-summary-button'
        className='diningBehaviorSummary'
        data-testid='delivery-summary-valid'
        onClick={handleClick}
        type='button'
      >
        <div className='content'>
          <LocationIcon size='xs' accessibility='decorative' />
          <span className='diningBehaviorText'>
            {t('fulfillment-panel.delivery-to')}
            <strong>{displayAddress}</strong>
          </span>
        </div>
        <ChevronRightIcon
          size='xs'
          accessibility='semantic'
          aria-label={t('fulfillment-general.change-delivery-address')}
        />
      </button>
    )
  }

  return (
    <button
      id='delivery-address-summary-button'
      className='diningBehaviorSummary invalid'
      data-testid='delivery-summary-invalid'
      type='button'
      onClick={handleClick}
    >
      <div className='content'>
        <LocationIcon size='xs' accessibility='decorative' />
        <span className='diningBehaviorText'>
          {t('fulfillment-panel.confirm-delivery-range')}
        </span>
      </div>
      <ChevronRightIcon
        size='xs'
        accessibility='semantic'
        aria-label={t('fulfillment-panel.set-delivery-address')}
      />
    </button>
  )
}

const PickupSummary = () => {
  const [changeLocationModalOpen, setChangeLocationModalOpen] = useState(false)
  const ooLocations = useOOLocations()
  const { selectedLocation } = useRestaurant()
  const multiLocation = ooLocations.length > 1
  const { track } = useTracker()
  const { selectedFulfillmentType } = useFulfillment()

  return (
    <>
      <button
        className='diningBehaviorSummary'
        data-testid='pickup-summary'
        disabled={!multiLocation}
        onClick={() => {
          track(TrackingEvents.CHANGE_LOCATION_BUTTON_CLICKED, {
            currentRestaurantGuid: selectedLocation?.externalId,
            page: 'order_page'
          })
          track(TrackingEvents.DINING_BEHAVIOR_SUMMARY_CLICKED, {
            diningOption: DiningOptionBehavior.TakeOut,
            diningOptionStatus: 'valid',
            fulfillmentTimeType: selectedFulfillmentType ?? undefined
          })
          setChangeLocationModalOpen(true)
        }}
        type='button'
      >
        <div className='content'>
          <LocationIcon size='xs' accessibility='decorative' />
          <span className='diningBehaviorText'>
            <Trans
              i18nKey='fulfillment-panel.pickup-from'
              components={{ strong: <strong /> }}
              values={{ address: formatAddress(selectedLocation) }}
            />
          </span>
        </div>
        {multiLocation && (
          <ChevronRightIcon
            size='xs'
            accessibility='semantic'
            aria-label={t('fulfillment-general.change-pickup-location')}
          />
        )}
      </button>
      <ChangeLocationModal
        isOpen={changeLocationModalOpen}
        onClose={() => setChangeLocationModalOpen(false)}
      />
    </>
  )
}

export default DiningBehaviorSummary
